import React, { useState, useEffect } from 'react';
import { FaMapMarkerAlt, FaCrosshairs, FaTimes, FaDirections } from 'react-icons/fa';
import { API_BASE_URL } from '../../config';

const DiscoverTab = () => {
    const [activeTab, setActiveTab] = useState('nearest'); // 'club' or 'nearest'
    const [clubProjects, setClubProjects] = useState([]);
    const [nearestProjects, setNearestProjects] = useState([]);
    
    const [geoLoading, setGeoLoading] = useState(false);
    const [geoError, setGeoError] = useState('');
    const [selectedProject, setSelectedProject] = useState(null);
    const [clubLoading, setClubLoading] = useState(false);

    useEffect(() => {
        fetchClubProjects();
    }, []);

    const fetchClubProjects = async () => {
        setClubLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/member_actions.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ action: 'get_events' }),
            });
            const data = await res.json();
            if (data.success) {
                setClubProjects(data.data.events);
            }
        } catch (error) {
            console.error('Error fetching club projects:', error);
        } finally {
            setClubLoading(false);
        }
    };

    const fetchNearestProjects = async () => {
        if (!navigator.geolocation) {
            setGeoError("Geolocation is not supported by your browser");
            return;
        }
        
        setGeoLoading(true);
        setGeoError('');
        
        navigator.geolocation.getCurrentPosition(
            async (position) => {
                const lat = position.coords.latitude;
                const lng = position.coords.longitude;
                
                try {
                    const res = await fetch(`${API_BASE_URL}/member_actions.php`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include',
                        body: JSON.stringify({ 
                            action: 'get_nearest_projects',
                            latitude: lat,
                            longitude: lng
                        }),
                    });
                    const data = await res.json();
                    if (data.success) {
                        setNearestProjects(data.data.projects);
                    } else {
                        setGeoError(data.message || "Failed to retrieve nearest projects");
                    }
                } catch (err) {
                    setGeoError("Network error while reaching servers");
                } finally {
                    setGeoLoading(false);
                }
            },
            (error) => {
                setGeoError("Unable to retrieve your location. Please grant permission.");
                setGeoLoading(false);
            }
        );
    };

    const handleTabSwitch = (tab) => {
        setActiveTab(tab);
        if (tab === 'nearest' && nearestProjects.length === 0 && !geoLoading) {
            fetchNearestProjects();
        }
    };

    const displayProjects = activeTab === 'club' ? clubProjects : nearestProjects;

    return (
        <div className="max-w-6xl mx-auto animate-fade-in">
            <div className="flex flex-col sm:flex-row justify-end items-center mb-6 gap-4">
                <div className="flex bg-slate-100 p-1.5 rounded-2xl w-full sm:w-auto shadow-sm border border-slate-200">
                    <button
                        onClick={() => handleTabSwitch('my-club')}
                        className={`flex-1 sm:flex-none px-8 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === 'my-club' ? 'bg-white text-indigo-600 shadow-md transform scale-105' : 'text-slate-500 hover:text-slate-800'}`}
                    >
                        My Club
                    </button>
                    <button
                        onClick={() => handleTabSwitch('nearest')}
                        className={`flex-1 sm:flex-none px-8 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === 'nearest' ? 'bg-white text-indigo-600 shadow-md transform scale-105' : 'text-slate-500 hover:text-slate-800'}`}
                    >
                        Nearest
                    </button>
                </div>
            </div>

            {activeTab === 'nearest' && (
                <div className="mb-8 bg-indigo-50/50 p-6 rounded-3xl border border-indigo-100 flex flex-col sm:flex-row items-center justify-between gap-4 shadow-sm">
                    <div className="text-center sm:text-left">
                        <h3 className="text-lg font-black text-indigo-900 mb-1 flex items-center justify-center sm:justify-start gap-2 uppercase tracking-tight">
                            <FaMapMarkerAlt className="text-indigo-500" /> Geography Scanner
                        </h3>
                        <p className="text-xs text-indigo-700/80 font-bold">Triangulate your position to automatically discover and audit regional Leo operations broadcasting GPS coordinates.</p>
                        {geoError && <p className="text-xs text-rose-500 mt-2 font-black uppercase tracking-widest">{geoError}</p>}
                    </div>
                    <button 
                        onClick={fetchNearestProjects} 
                        disabled={geoLoading}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-4 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all flex items-center justify-center gap-3 disabled:opacity-50 shrink-0 shadow-[0_10px_30px_rgba(79,70,229,0.3)] hover:shadow-[0_15px_40px_rgba(79,70,229,0.4)] hover:-translate-y-1 w-full sm:w-auto"
                    >
                        <FaCrosshairs size={14} className={geoLoading ? 'animate-spin' : ''} /> 
                        {geoLoading ? 'Scanning...' : 'Initialize Scan'}
                    </button>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {displayProjects.length === 0 && !geoLoading && !clubLoading && (
                    <div className="col-span-full text-center py-20 text-slate-400 font-bold uppercase tracking-widest text-[10px] bg-white rounded-3xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.02)]">
                        {activeTab === 'nearest' ? 'Awaiting coordinate triangulation or no nearby projects found' : 'No localized club projects detected'}
                    </div>
                )}
                
                {((geoLoading && activeTab === 'nearest') || (clubLoading && activeTab === 'my-club')) && (
                    <div className="col-span-full flex flex-col items-center justify-center py-24 bg-white rounded-3xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.02)]">
                        <div className="w-10 h-10 rounded-full border-4 border-indigo-100 border-t-indigo-600 animate-spin mb-6"></div>
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] animate-pulse">
                            {activeTab === 'nearest' ? 'Triangulating Global Position...' : 'Synchronizing Local Data...'}
                        </span>
                    </div>
                )}

                {(!geoLoading && !clubLoading) && displayProjects.map((project) => (
                    <div 
                        key={project.id} 
                        onClick={() => setSelectedProject(project)}
                        className="bg-white p-6 rounded-3xl border border-slate-100 flex flex-col justify-between group hover:-translate-y-2 hover:shadow-[0_20px_50px_rgba(0,0,0,0.08)] transition-all duration-500 relative overflow-hidden cursor-pointer"
                    >
                        {project.distance !== undefined && (
                            <div className="absolute top-0 right-0 bg-indigo-600 text-white text-[9px] font-black tracking-widest px-4 py-1.5 rounded-bl-2xl z-10 shadow-md flex items-center gap-1.5">
                                <FaDirections /> {parseFloat(project.distance).toFixed(1)} KM
                            </div>
                        )}
                        <div>
                            <div className="flex justify-between items-start mb-4">
                                <h3 className="font-black text-lg text-slate-900 line-clamp-2 leading-tight pr-16 group-hover:text-indigo-600 transition-colors uppercase tracking-tight">{project.name}</h3>
                            </div>
                            <p className="text-slate-500 text-xs mb-6 line-clamp-3 leading-relaxed font-bold">
                                {project.description || 'No operational description synchronized.'}
                            </p>
                        </div>
                        <div className="flex items-center gap-2 text-[9px] font-black text-slate-400 uppercase tracking-widest border-t border-slate-50 pt-5">
                            <span className="text-indigo-600 truncate bg-indigo-50 px-2 py-1 rounded-lg">{project.club_name}</span>
                            <span>•</span>
                            <span className="truncate">{project.sub_district_name}</span>
                        </div>
                    </div>
                ))}
            </div>

            {/* Project Details Modal */}
            {selectedProject && (
                <div className="fixed inset-0 z-[100000] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md animate-in fade-in duration-300" onClick={() => setSelectedProject(null)}>
                    <div className="bg-white w-full max-w-lg overflow-hidden shadow-2xl transform transition-all animate-premium relative rounded-[2rem]" onClick={e => e.stopPropagation()}>
                        <div className="p-8 border-b border-slate-100 flex justify-between items-start bg-slate-50/80">
                            <div>
                                <h3 className="text-2xl font-black text-slate-900 uppercase tracking-tighter pr-4 leading-none">{selectedProject.name}</h3>
                                <div className="flex items-center gap-2 mt-3">
                                    <span className="bg-indigo-100 text-indigo-700 font-black text-[9px] px-3 py-1 rounded-xl uppercase tracking-widest">{selectedProject.club_name}</span>
                                    <span className="text-slate-400 font-bold text-[10px] uppercase tracking-widest">• {selectedProject.sub_district_name}</span>
                                </div>
                            </div>
                            <button onClick={() => setSelectedProject(null)} className="w-10 h-10 flex shrink-0 items-center justify-center rounded-2xl bg-white text-slate-400 hover:text-slate-900 hover:scale-105 shadow-sm border border-slate-100 hover:bg-slate-50 transition-all">
                                <FaTimes size={14} />
                            </button>
                        </div>
                        <div className="p-8 space-y-8">
                            <div>
                                <h4 className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-3">
                                    <span className="w-2 h-2 rounded-full bg-slate-300"></span> Project Brief
                                </h4>
                                <p className="text-sm text-slate-700 leading-relaxed font-semibold">{selectedProject.description || 'No operational description available.'}</p>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4">
                                <div className="p-5 bg-slate-50 border border-slate-100 rounded-3xl flex flex-col justify-center">
                                    <h4 className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Created On</h4>
                                    <p className="text-sm font-black text-slate-900">{new Date(selectedProject.created_at).toLocaleDateString()}</p>
                                </div>
                                {selectedProject.distance !== undefined && (
                                    <div className="p-5 bg-indigo-50 border border-indigo-100 rounded-3xl flex flex-col justify-center">
                                        <h4 className="text-[9px] font-black text-indigo-500 uppercase tracking-widest mb-1.5">Raw Distance</h4>
                                        <p className="text-sm font-black text-indigo-900">{parseFloat(selectedProject.distance).toFixed(2)} KM</p>
                                    </div>
                                )}
                            </div>

                            {(selectedProject.latitude && selectedProject.longitude) ? (
                                <button 
                                    onClick={() => window.open(`https://www.google.com/maps/dir/?api=1&destination=${selectedProject.latitude},${selectedProject.longitude}`, '_blank')}
                                    className="w-full flex items-center justify-center gap-3 py-5 bg-indigo-600 text-white font-black text-[11px] uppercase tracking-[0.2em] shadow-[0_10px_30px_rgba(79,70,229,0.3)] hover:shadow-[0_15px_40px_rgba(79,70,229,0.4)] hover:-translate-y-1 transition-all rounded-3xl"
                                >
                                    <FaDirections size={18} /> Get Live Directions
                                </button>
                            ) : (
                                <div className="p-5 bg-orange-50 border border-orange-100 text-center rounded-3xl">
                                    <p className="text-[10px] font-black text-orange-600 uppercase tracking-[0.1em] flex items-center justify-center gap-2">
                                        <FaMapMarkerAlt /> No exact GPS location mapped
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DiscoverTab;
